"use client";
import React, { useMemo, useState, useEffect } from "react";
import { Empty, Tabs, TabPane, Collapse, List } from "@douyinfe/semi-ui";
import { IconRefresh, IconChevronRight } from "@douyinfe/semi-icons";
import {
  GuideMenuItemType,
  ChatContentType,
  ComponentTypeEnum,
} from "@/app/types/typeConfig/chatQuestion";
import {
  ReportQueuePropsType,
  ReportBizTypeEnum,
  ReportEventEnum,
  ReportContentEnum,
  ReportParentContentEnum,
  ReportSceneTypeEnum,
  RobotChatPropsType,
  ReportKeywordContentEnum,
} from "@/app/types/typeConfig/report";
import {
  MessageSender,
  MessageType,
  HeaderType,
  RobotMessageType,
  MessageStatus,
} from "@/app/store/types";
import DangerouslySetInnerHTML from "@/app/ui/dangerouslySetInnerHTML";
import { AIQuestionList } from "@/app/hooks/useClientInit";
import styles from "./index.module.scss";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import { handleRobotChat } from "@/app/utils/bizRequest";
import { useCurrentLocale } from "@/app/lib/LocaleProvider";
import { useAuth } from "@/app/lib/AuthProvider";
import { createUuid } from "@/app/utils/string";
import { useMessageStore } from "@/app/store";
// import { Tabs } from "antd";
import Image from "next/image";
interface GuideMenuProps {
  data?: AIQuestionList;
  onQuestionClick?: (item: GuideMenuItemType) => void;
}

const GuideMenu: React.FC<GuideMenuProps> = ({ data }) => {
  const { addToReportQueue, requireReportProps } = useReportRequestQueue();
  const locale = useCurrentLocale();
  const { isAuthenticated, user } = useAuth();
  const { setRobotMessage, addOutgoingMessage } = useMessageStore();
  const ITEMS_PER_PAGE = 5;

  // 为每个分类维护当前页码
  const [pageIndexMap, setPageIndexMap] = useState<Record<string, number>>({});

  // 追踪当前激活的 tab
  const [activeKey, setActiveKey] = useState<string>("");

  // 检测是否为移动端
  const [isMobile, setIsMobile] = useState(false);

  // 移动端折叠面板激活的 key
  const [collapseActiveKey, setCollapseActiveKey] = useState<string[]>([]);

  const categorizedQuestions = useMemo(() => {
    if (!data?.list || data.list.length === 0) {
      return [];
    }
    return data.list;
  }, [data]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 当数据加载后，设置默认激活第一个 tab
  useEffect(() => {
    if (categorizedQuestions.length > 0 && !activeKey) {
      setActiveKey(String(categorizedQuestions[0].id));
    }
  }, [categorizedQuestions, activeKey]);

  // 获取分页后的问题列表
  const getPaginatedItems = (items: GuideMenuItemType) => {
    const allConfigItems = items.configRelateVOList;
    if (!Array.isArray(allConfigItems)) {
      return [];
    }
    if (allConfigItems.length === 0) {
      return [];
    }

    const currentPage = pageIndexMap[items.id] || 0;
    const totalPages = Math.ceil(allConfigItems.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage % totalPages) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    return allConfigItems.slice(startIndex, endIndex);
  };

  // 刷新按钮点击处理
  const handleRefresh = () => {
    if (!activeKey) return;

    // 找到当前激活的分类
    const currentCategory = categorizedQuestions.find(
      (cat) => String(cat.id) === activeKey,
    );

    if (!currentCategory || !currentCategory.configRelateVOList) return;

    const totalItems = currentCategory.configRelateVOList.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    // 如果只有一页或没有数据，不需要切换
    if (totalPages <= 1) return;

    // 更新当前分类的页码，循环切换到下一页
    setPageIndexMap((prev) => ({
      ...prev,
      [currentCategory.id]: ((prev[currentCategory.id] || 0) + 1) % totalPages,
    }));
  };

  if (!data || !data.list || data.list.length === 0) {
    return (
      <div className={styles.guideMenuEmpty}>
        <Empty description="暂无引导问题" />
      </div>
    );
  }

  const handleQuestionClick = (
    item: GuideMenuItemType,
    question: GuideMenuItemType,
  ) => {
    // dispatch(updateTagType(item.tagType));
    const reportParams: ReportQueuePropsType = {
      bizType: ReportBizTypeEnum.GUIDE_MENU,
      content: item.title,
      contentId: item.id,
      contentType: item.relateType,
      createTime: new Date().getTime(),
      eventType: ReportEventEnum.CLICK,
      scene: ReportSceneTypeEnum.NORMAL_CONSULT,
    };
    if (ReportContentEnum.FAQ === item.relateType) {
      reportParams.bizType = ReportBizTypeEnum.FAQ;
      reportParams.parentContentId = item.id;
      reportParams.parentContentType = ReportParentContentEnum.GUIDE_MENU;
    } else if (ReportContentEnum.TAG === item.relateType) {
      reportParams.bizType = ReportBizTypeEnum.TAG;
      reportParams.parentContentId = item.id;
      reportParams.parentContentType = ReportParentContentEnum.GUIDE_MENU;
    }
    addToReportQueue(reportParams);
    // const parentContentInfo = {
    //   parentContent: item.title,
    //   parentContentId: item.id,
    //   parentContentType: ReportParentContentEnum.GUIDE_MENU,
    // };
    switch (item.relateType) {
      // case ReportContentEnum.TECH_AI:
      //   dispatch(
      //     batchAddQuestion([requireCustomerQuestionParams(item.title)]),
      //   );
      //   dispatch(triggerTryOpenAI(true));
      //   break;
      // case ReportContentEnum.EXTERNAL_LINK:
      //   openUrl(item.relateContent);
      //   break;
      // case ReportContentEnum.CATEGORY:
      //   queryCategoryDetail({
      //     categoryId: item.relateId,
      //     language: lng,
      //   }).then((res) => {
      //     dispatch(
      //       batchAddQuestion([
      //         requireCustomerQuestionParams(item.title),
      //         requireRobotQuestionParams(
      //           res?.questions || [],
      //           TemplateFlagEnum.SelectQuestion,
      //           {
      //             type: ComponentTypeEnum.Robot,
      //             id: item.id,
      //             parentContentInfo,
      //             rootSourceType: item.tagType,
      //           },
      //         ),
      //       ]),
      //     );
      //   });
      //   break;
      // case ReportContentEnum.TAG:
      //   if (item?.commonLanguageVO?.externalLink) {
      //     openUrl(item.commonLanguageVO.externalLink);
      //   } else {
      //     handleAnswerQuestion(item, parentContentInfo, 2);
      //   }
      //   break;
      // case ReportContentEnum.FAQ:
      //   handleAnswerQuestion(item, parentContentInfo, 1);
      //   break;
      case ReportContentEnum.HOT_QUESTION:
        HotQuestionClick(item, question);
        break;
    }
  };

  const HotQuestionClick = (
    item: GuideMenuItemType,
    question: GuideMenuItemType,
  ) => {
    const params: RobotChatPropsType = {
      id: question.relateId,
      language: locale,
      idType: 1,
      data: {
        userId: isAuthenticated ? String(user?.cusId) : createUuid(),
      },
      behaviorCollect: requireReportProps({
        bizType: ReportBizTypeEnum.HOT_QUESTION,
        content: question.relateContent,
        contentId: question.relateId,
        contentType: ReportKeywordContentEnum.NAVIGATION_RECOMMENDATIONS,
        parentContentType: ReportParentContentEnum.HOT_QUESTION,
      }),
    };
    handleRobotChat(
      params,
      {
        customerMessage: question.relateContent,
        parentContentInfo: {
          parentContentId: "",
          parentContent: item.title,
          parentContentType: ReportParentContentEnum.HOT_QUESTION,
        },
      },
      isAuthenticated,
    ).then((res) => {
      const chatContent = res as ChatContentType[];
      chatContent.forEach((contentItem) => {
        if (contentItem.type === ComponentTypeEnum.Customer) {
          addOutgoingMessage({
            headerType: HeaderType.MESSAGE,
            clientId: createUuid(),
            content: `{"text":"${contentItem.message}"}`,
            senderType: MessageSender.USER,
            type: MessageType.Text,
            timestamp: Date.now(),
            status: MessageStatus.Success,
          });
        } else {
          setRobotMessage({
            id: contentItem.id,
            type: MessageType.Text,
            headerType: HeaderType.MESSAGE,
            senderType: MessageSender.ROBOT,
            messageType: RobotMessageType.ROBOT_ACTION_CARD,
            showFeedFooter: true,
            content: "",
            timestamp: Date.now(), // 稍微延迟，确保在欢迎语之后
            senderName: "智能助手",
            robotData: {
              // 机器人相关数据
              ...contentItem,
            },
          });
        }
      });
    });
  };

  // 移动端渲染 - 使用 Collapse 折叠面板
  const renderMobileView = () => {
    return (
      <div className={`${styles.guideMenu} ${styles.mobileGuideMenu}`}>
        <div className={styles.guideMenuHeader}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {data.content && (
              <DangerouslySetInnerHTML msgContent={data.content} />
            )}
          </div>
        </div>
        <Collapse
          accordion={false}
          className={styles.mobileCollapse}
          activeKey={collapseActiveKey}
          onChange={(key) => setCollapseActiveKey(key as string[])}
        >
          {categorizedQuestions.map((category) => {
            const paginatedItems = getPaginatedItems(category);
            return (
              <Collapse.Panel
                key={String(category.id)}
                header={
                  <div className={styles.collapsePanelHeader}>
                    {category.fileUrl && (
                      <div className={styles.panelIcon}>
                        <Image
                          src={category.fileUrl}
                          width={40}
                          height={40}
                          alt={category.title}
                        />
                      </div>
                    )}
                    <div className={styles.panelContent}>
                      <span className={styles.panelTitle}>
                        {category.title}
                      </span>
                      {category.description && (
                        <span className={styles.panelDescription}>
                          {category.description}
                        </span>
                      )}
                    </div>
                  </div>
                }
                itemKey={String(category.id)}
              >
                <List
                  className={styles.mobileList}
                  dataSource={paginatedItems}
                  split={false}
                  renderItem={(configItem, index) => (
                    <List.Item
                      key={`${configItem.id}-${index}`}
                      className={styles.mobileListItem}
                      onClick={() =>
                        handleQuestionClick(
                          category,
                          configItem as GuideMenuItemType,
                        )
                      }
                      main={
                        <div className={styles.listItemContent}>
                          <span className={styles.listItemText}>
                            {configItem.relateContent}
                          </span>
                        </div>
                      }
                      extra={
                        <IconChevronRight
                          size="large"
                          className={styles.listItemArrow}
                        />
                      }
                    />
                  )}
                  emptyContent={
                    <div className={styles.emptyState}>暂无相关内容</div>
                  }
                />
                {paginatedItems.length > ITEMS_PER_PAGE && (
                  <div className={styles.mobileRefreshWrapper}>
                    <button
                      className={styles.mobileRefreshBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefresh();
                      }}
                    >
                      <IconRefresh />
                      <span>换一批</span>
                    </button>
                  </div>
                )}
              </Collapse.Panel>
            );
          })}
        </Collapse>
      </div>
    );
  };

  // PC端渲染 - 使用 Tabs
  const renderPCView = () => {
    return (
      <div className={styles.guideMenu}>
        <div className={styles.guideMenuHeader}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {data.content && (
              <DangerouslySetInnerHTML msgContent={data.content} />
            )}
          </div>
          <IconRefresh
            style={{ cursor: "pointer" }}
            onClick={() => handleRefresh()}
          />
        </div>
        <Tabs
          collapsible
          className={styles.guideMenuTabs}
          activeKey={activeKey}
          onChange={(key) => setActiveKey(key as string)}
        >
          {categorizedQuestions.map((category) => {
            const paginatedItems = getPaginatedItems(category);
            return (
              <TabPane
                key={category.id}
                tab={category.title}
                itemKey={String(category.id)}
              >
                <div className={styles.tabPaneContent}>
                  <ul className={styles.configList}>
                    {paginatedItems.map((configItem, index) => (
                      <li
                        key={`${configItem.id}-${index}`}
                        className={styles.configListItem}
                        onClick={() =>
                          handleQuestionClick(
                            category,
                            configItem as GuideMenuItemType,
                          )
                        }
                      >
                        <span className={styles.configItemText}>
                          {configItem.relateContent}
                        </span>
                        <div className={styles.arrowIcon}>
                          <Image
                            src="/images/arrow-right-fill.svg"
                            height={20}
                            width={20}
                            alt=""
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                  {paginatedItems.length === 0 && (
                    <div className={styles.emptyState}>暂无相关内容</div>
                  )}
                </div>
              </TabPane>
            );
          })}
        </Tabs>
      </div>
    );
  };

  return isMobile ? renderMobileView() : renderPCView();
};

export default GuideMenu;
